import type { Card } from "../entities/types.ts";
import {
  isPayloadCard,
  getPayloadTargetMode,
} from "../game/cardEffectHandlers.ts";

export type PersistenceKind = "instant" | "payload" | "duration" | "permanent";

export interface PersistenceTag {
  label: string;
  kind: PersistenceKind;
}

/**
 * Derive how long a drafted card occupies a loadout slot, for the draft/skill
 * UI persistence badge:
 *  - payload (global) → ACTIVE   (armed in rack, deployed by player)
 *  - payload (aimed)  → ACTIVE · ◎ AIM (requires targeting)
 *  - instant target   → INSTANT  (fires immediately, never takes a slot)
 *  - duration set     → {N}W     (holds a slot for N waves, then expires)
 *  - otherwise        → PERMANENT (holds a slot indefinitely)
 */
export function getPersistenceTag(card: Card): PersistenceTag {
  if (isPayloadCard(card)) {
    const mode = getPayloadTargetMode(card);
    const label = mode && mode !== "global" ? "ACTIVE · ◎ AIM" : "ACTIVE";
    return { label, kind: "payload" };
  }
  if (card.effect.target === "instant") {
    return { label: "INSTANT", kind: "instant" };
  }
  if (card.duration) {
    return { label: `${card.duration}W`, kind: "duration" };
  }
  return { label: "PERMANENT", kind: "permanent" };
}
